import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle,
  ChevronRight,
  Printer,
  AlertCircle,
  FileText,
  ZoomIn,
} from "lucide-react";
import { DownloadIcon, RefreshCWIcon } from "../../components/icons";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import PrintCertificatePreview from "../../common/PrintCertificatePreview/PrintCertificatePreview";
import PrintRequestModal from "../../common/PrintRequestModal/PrintRequestModal";
import { GET, POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import { renderPdfThumbnail } from "../../common/PrintCertificatePreview/pdfPreview";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import EntityConstants from "../../configs/EntityConstants";
import DownloadRequestModal from "../../common/DownloadRequestModal/DownloadRequestModal";
import useNavigationStore from "../../store/navigationStore";
import { useTranslation } from "react-i18next";
import { handleRovingTabKeyDown } from "../../utils/keyboardNavigation";

const PRINT_REQUESTS_POLLING_INTERVAL = 3000;

export default function PrintPdf() {
  const { legacyTransactionId: paramLegacyTransactionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const legacyTransactionId =
    paramLegacyTransactionId || localStorage.getItem("legacyTransactionId");

  const credentialTemplateId = localStorage.getItem("credentialTemplateId");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeTab, setActiveTab] = useState("all");
  const [showPrintRequestModal, setShowPrintRequestModal] = useState(false);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [certificateName, setCertificateName] = useState("");

  useEffect(() => {
    if (!legacyTransactionId) return;

    const fetchCertificateName = async () => {
      try {
        const res = await GET(
          `${EntityConstants.GET_USER_PREVIEW_SVG}?issuer_credential_template_id=${legacyTransactionId}`,
        );
        const data = await res.json();
        setCertificateName(data.certificate_name || "");
      } catch (error) {
        console.debug("Failed to fetch certificate name:", error);
      }
    };

    fetchCertificateName();
  }, [legacyTransactionId]);

  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [documentData, setDocumentData] = useState({
    total_recipients: 0,
    certificate_preview: "",
  });
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [printRequestsData, setPrintRequestsData] = useState({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });
  const [offset, setOffset] = useState(0);
  const [showDownloadRequestModal, setShowDownloadRequestModal] =
    useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [isPollingActive, setIsPollingActive] = useState(false);
  const limit = 5;

  const [isPreviewFailed, setIsPreviewFailed] = useState(false);
  const previewRequestRef = useRef(0);

  useEffect(() => {
    if (!currentProgramme || !currentBatch || !legacyTransactionId) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, legacyTransactionId, navigate]);

  const loadPreviewImage = async (url) => {
    const request = previewRequestRef.current + 1;
    previewRequestRef.current = request;
    setIsPreviewFailed(false);
    setIsPdfLoading(true);
    try {
      const { dataUrl } = await renderPdfThumbnail(url);
      if (previewRequestRef.current === request) setPreviewImageUrl(dataUrl);
    } catch (error) {
      console.error("Error rendering PDF:", error);
      if (previewRequestRef.current === request) {
        setPreviewImageUrl(null);
        setIsPreviewFailed(true);
      }
    } finally {
      if (previewRequestRef.current === request) setIsPdfLoading(false);
    }
  };

  useEffect(() => {
    const fetchDocumentData = async () => {
      if (!legacyTransactionId) {
        return;
      }
      try {
        const response = await GET(
          `${EntityConstants.GET_USER_DETAILS_PRINT_CERTIFICATES}?issuer_credential_template_id=${legacyTransactionId}`,
        );
        const data = await response.json();
        setDocumentData(data);
        if (data.certificate_preview) {
          await loadPreviewImage(data.certificate_preview);
        }
      } catch (error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_fetch_document_data"),
        });
      }
    };

    fetchDocumentData();
  }, [legacyTransactionId]);

  const handleCreatePrintRequest = async () => {
    setIsTableLoading(true);
    try {
      const response = await POST(
        `${EntityConstants.POST_REQUEST_CERTIFICATE}`,
        {
          issuer_credential_template_id: legacyTransactionId,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create print request");
      }
      Toast({
        type: "success",
        title: t("workspace:success"),
        message: t("workspace:print_request_created_successfully"),
      });
      setShowDownloadRequestModal(true);
      setShowPrintRequestModal(false);

      const newData = await fetchPrintRequests(
        activeTab === "all" ? "" : activeTab,
        searchTerm,
        offset,
        false,
      );

      startPollingIfNeeded(newData);
    } catch (error) {
      console.error("Error creating print request:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_create_print_request"),
      });
    } finally {
      setIsTableLoading(false);
    }
  };

  const fetchPrintRequests = async (
    section = "",
    search = "",
    offset = 0,
    showLoading = true,
  ) => {
    if (!legacyTransactionId) {
      showLoading && setIsTableLoading(false);
      return;
    }
    showLoading && setIsTableLoading(true);
    try {
      let url = `${EntityConstants.GET_USER_PDF_DETAILS}?issuer_credential_template_id=${legacyTransactionId}&limit=${limit}&offset=${offset}`;
      if (section) url += `&status=${section}`;
      if (search) url += `&search=${search}`;

      const response = await GET(url);
      const data = await response.json();
      setPrintRequestsData(data);
      return data;
    } catch (error) {
      console.error("Error fetching print requests:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_print_requests"),
      });
      throw error;
    } finally {
      showLoading && setIsTableLoading(false);
    }
  };

  const needsPolling = (results) => {
    if (!results || results.length === 0) return false;
    return results.some(
      (request) =>
        request.status === "Started" || request.status === "In-Progress",
    );
  };

  const startPollingIfNeeded = (data) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
      setIsPollingActive(false);
    }

    if (needsPolling(data.results)) {
      setIsPollingActive(true);
      const interval = setInterval(async () => {
        try {
          const newData = await fetchPrintRequests(
            activeTab === "all" ? "" : activeTab,
            searchTerm,
            offset,
            false,
          );
          const shouldStopPolling = !needsPolling(newData.results);

          if (shouldStopPolling) {
            clearInterval(interval);
            setPollingInterval(null);
            setIsPollingActive(false);

            const hasCompletedRequests = newData.results.some(
              (request) => request.status === "Completed",
            );

            if (hasCompletedRequests) {
              Toast({
                type: "success",
                title: t("workspace:print_request_completed"),
                message: t("workspace:print_request_processed_successfully"),
                persistent: true,
              });
            }
          }
        } catch (error) {
          console.error("Polling error:", error);
          clearInterval(interval);
          setPollingInterval(null);
          setIsPollingActive(false);
        }
      }, PRINT_REQUESTS_POLLING_INTERVAL);

      setPollingInterval(interval);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchPrintRequests(
        activeTab === "all" ? "" : activeTab,
        searchTerm,
        offset,
        true,
      );
      startPollingIfNeeded(data);
    };

    fetchData();
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [activeTab, searchTerm, offset, legacyTransactionId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchPrintRequests(activeTab === "all" ? "" : activeTab, searchTerm, 0);
  };

  const handleNext = () => {
    if (printRequestsData.next) {
      setOffset(offset + limit);
    }
  };

  const handlePrevious = () => {
    if (printRequestsData.previous) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleNewPrintRequest = () => {
    navigate(`/issuer/credentials/${legacyTransactionId}/print-request`);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };
  const handleWorkspaceClick = () => {
    navigate("/issuer/workspace");
  };
  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handlePendingIssuanceClick = () => {
    navigate(
      `/issuer/workspace/batches/summary/${credentialTemplateId}/${legacyTransactionId}`,
    );
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback(e);
    }
  };

  const handlePreviewClick = () => {
    if (documentData.certificate_preview && previewImageUrl) {
      setIsPreviewModalOpen(true);
    }
  };

  const printRequestTabs = [
    { id: "all", label: t("workspace:all_requests") },
    { id: "completed", label: t("workspace:done") },
    { id: "in-progress", label: t("workspace:pending") },
    { id: "failed", label: t("workspace:failed") },
  ];
  const printRequestTabIds = printRequestTabs.map((tab) => tab.id);

  const handlePrintTabChange = (tabId) => {
    setActiveTab(tabId);
    setOffset(0);
  };

  const handlePrintTabKeyDown = (event, tabId) => {
    handleRovingTabKeyDown({
      event,
      items: printRequestTabIds,
      currentItem: tabId,
      onChange: handlePrintTabChange,
      getElementId: (nextTabId) => `print-pdf-tab-${nextTabId}`,
    });
  };

  const recentFiles = printRequestsData.results.slice(0, 4).map((file) => ({
    name: file.file_name,
    url: file.pdf_download,
    date: file.requested_at,
  }));

  return (
    <div
      className="min-h-screen mt-20 bg-cfSecondary w-full"
      id={pageRootTestIds.printPdf}
    >
      {/* Breadcrumbs */}
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col lg:flex-row items-start lg:items-center min-h-[4rem] justify-between w-full py-2`}
        >
          <div className="min-w-0 w-full">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-start min-h-[2.5rem] lg:min-h-[4rem] w-full min-w-0">
                <div className="flex items-center gap-2 text-sm text-gray-500 truncate min-w-0 max-w-full">
                  <button
                    id="print-pdf-breadcrumb-workspace"
                    onClick={handleWorkspaceClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleWorkspaceClick();
                      }
                    }}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {t("workspace:workspace")}
                  </button>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="print-pdf-breadcrumb-programme"
                    onClick={handleProgrammeClick}
                    onKeyDown={(e) => handleKeyDown(e, handleProgrammeClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer breadcrumb-item"
                    title={
                      currentProgramme.programme_name ||
                      t("workspace:programme")
                    }
                  >
                    {currentProgramme.programme_name ||
                      t("workspace:programme")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="print-pdf-breadcrumb-batch"
                    onClick={handleBatchClick}
                    onKeyDown={(e) => handleKeyDown(e, handleBatchClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer breadcrumb-item"
                    title={currentBatch.batch_name || t("workspace:batch")}
                  >
                    {currentBatch.batch_name || t("workspace:batch")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />

                  <span
                    id="print-pdf-breadcrumb-certificate"
                    onClick={handlePendingIssuanceClick}
                    onKeyDown={(e) =>
                      handleKeyDown(e, handlePendingIssuanceClick)
                    }
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer breadcrumb-item"
                    title={certificateName || t("workspace:loading")}
                  >
                    {certificateName ? certificateName : t("workspace:loading")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />

                  <span className="text-gray-800 font-medium">
                    {t("workspace:pdf_print")}
                  </span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between my-4 px-4 gap-3">
        <div className="flex gap-2 justify-start">
          <div
            id="print-pdf-back-button"
            onClick={handlePendingIssuanceClick}
            onKeyDown={(e) => handleKeyDown(e, handlePendingIssuanceClick)}
            role="button"
            tabIndex={0}
            className="mt-[0.2rem] cursor-pointer"
          >
            <img src={ArrowLeftIcon} alt="" className="" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {t("workspace:print_certificates")}
            </h1>
            <p className="text-gray-500">
              {t("workspace:select_recipients_and_download")}
            </p>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            id="print-pdf-new-print-request"
            onClick={handleNewPrintRequest}
            className="flex items-center justify-center px-3 py-2 border border-transparent rounded-[8px] shadow-sm text-md font-medium text-white bg-cfPrimaryButton hover:bg-cfPrimaryDark w-full sm:w-auto"
          >
            <Printer className="mr-2 h-4 w-4" />
            {t("workspace:new_print_request")}
          </button>
        </div>
      </div>

      {/* Stats Cards and Preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 px-4">
        {/* Total Recipients Widget */}
        <div className="bg-white border rounded-xl p-4 shadow-sm flex flex-col items-center justify-center">
          <div className="text-base text-gray-500 mb-2">
            {t("workspace:total_recipients")}
          </div>
          <div
            id="print-pdf-total-recipients"
            className="text-3xl font-bold text-center"
          >
            {documentData.total_recipients ?? <CfLoader size="small" />}
          </div>
        </div>

        {/* Recent Files Widget */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="text-sm text-gray-500 mb-2">
            {t("workspace:recent_files")}
          </div>
          <div className="space-y-2 overflow-y-auto">
            {recentFiles.length > 0 ? (
              recentFiles.map((file, index) => (
                <div
                  key={index}
                  id={`print-pdf-recent-file-${index}`}
                  className="flex items-center justify-between bg-gray-50 p-2 rounded-md"
                >
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-indigo-600 mr-2" />
                    <span className="text-sm truncate max-w-[250px]">
                      {file.name ? file.name : t("workspace:unavailable")}
                    </span>
                  </div>
                  {file.url ? (
                    <a
                      id={`print-pdf-recent-file-download-${index}`}
                      href={file.url}
                      download
                      aria-label={t("workspace:download")}
                      className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                    >
                      <DownloadIcon
                        size={16}
                        className="inline-flex"
                        aria-hidden
                      />
                    </a>
                  ) : (
                    <button
                      className="h-8 w-8 p-0 text-gray-300 cursor-not-allowed flex items-center justify-center"
                      disabled
                      aria-label={t("workspace:download")}
                    >
                      <DownloadIcon
                        size={16}
                        className="inline-flex"
                        aria-hidden
                      />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-md text-gray-600 font-semibold pt-[5rem] text-center">
                {t("workspace:no_recent_files_found")}
              </div>
            )}
          </div>
        </div>

        {/* Document Preview */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="text-sm text-gray-500 mb-2">
            {t("workspace:document_preview")}
          </div>
          {isPdfLoading ? (
            <div className="aspect-[1.414] bg-gray-50 rounded-md overflow-hidden flex items-center justify-center w-3/4 mx-auto">
              <CfLoader size="small" />
            </div>
          ) : (
            <div
              id="print-pdf-document-preview"
              className={`aspect-[1.414] bg-gray-50 rounded-md overflow-hidden flex items-center justify-center w-3/4 mx-auto relative focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton ${
                previewImageUrl ? "cursor-zoom-in" : "cursor-default"
              }`}
              onMouseEnter={() => setIsPreviewHovered(true)}
              onMouseLeave={() => setIsPreviewHovered(false)}
              onFocus={() => setIsPreviewHovered(true)}
              onBlur={() => setIsPreviewHovered(false)}
              onClick={handlePreviewClick}
              onKeyDown={(e) => handleKeyDown(e, handlePreviewClick)}
              role="button"
              tabIndex={0}
            >
              {previewImageUrl ? (
                <div className="relative w-full h-full flex items-center justify-center p-2">
                  <img
                    src={previewImageUrl}
                    alt="Certificate preview"
                    className="max-h-full max-w-full object-contain bg-white shadow-md ring-1 ring-black/5"
                  />
                  <div
                    className={`absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity duration-200 ${
                      isPreviewHovered ? "opacity-100" : "opacity-0"
                    }`}
                    aria-hidden="true"
                  >
                    <div className="bg-white/90 rounded-full p-3 shadow">
                      <ZoomIn className="h-6 w-6 text-gray-700" />
                    </div>
                  </div>
                </div>
              ) : isPreviewFailed ? (
                <div className="flex flex-col items-center gap-2 px-3 text-center">
                  <span className="text-sm text-gray-500">
                    {t("workspace:preview_load_failed")}
                  </span>
                  <button
                    type="button"
                    id="print-pdf-document-preview-retry"
                    onClick={(e) => {
                      e.stopPropagation();
                      loadPreviewImage(documentData.certificate_preview);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-cfPrimaryButton px-3 py-1 text-sm font-medium text-cfPrimaryButton hover:bg-cfPrimaryButton hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton"
                  >
                    <RefreshCWIcon size={14} aria-hidden />
                    {t("workspace:retry_preview")}
                  </button>
                </div>
              ) : (
                <div className="text-gray-400">
                  {t("workspace:no_preview_available")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center mb-4 gap-4 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0">
          <h2 className="text-lg font-semibold">
            {t("workspace:print_requests")}
          </h2>
          <div className="flex items-center border border-gray-300 rounded-md overflow-x-auto max-w-full">
            <div
              className="flex min-w-max"
              role="tablist"
              aria-label={t("workspace:print_requests")}
            >
              {printRequestTabs.map((tab) => (
                <button
                  key={tab.id}
                  id={`print-pdf-tab-${tab.id}`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  onClick={() => handlePrintTabChange(tab.id)}
                  onKeyDown={(e) => handlePrintTabKeyDown(e, tab.id)}
                  className={`px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                    activeTab === tab.id
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              id="print-pdf-refresh-table"
              onClick={() => {
                fetchPrintRequests(
                  activeTab === "all" ? "" : activeTab,
                  searchTerm,
                  offset,
                  true,
                );
              }}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border-l border-gray-300 flex items-center justify-center"
              title="Refresh table"
            >
              <RefreshCWIcon
                size={16}
                className={`inline-flex ${isPollingActive ? "animate-spin" : ""}`}
                aria-hidden
              />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 w-full lg:w-auto">
          <form
            onSubmit={handleSearch}
            className={`bg-cfSecondary p-2 rounded-lg w-full lg:w-[25rem] lg:ml-[0.630rem] self-center ml-[0rem] lg:self-end lg:m-0 border border-gray-400`}
          >
            <input
              type="text"
              name="search"
              id="print-pdf-input-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("workspace:search_requests")}
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      {/* Print Requests Table */}
      <div className="rounded-lg overflow-hidden pb-4 px-4">
        {isTableLoading ? (
          <div className="flex justify-center items-center h-64">
            <CfLoader />
          </div>
        ) : printRequestsData.results.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-cfTableHeaderBackgroundColor h-[3.563rem] text-left">
                <tr>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                    onClick={() => handleSort("requestedBy")}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => handleSort("requestedBy"))
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">
                      {t("workspace:requested_by")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                    onClick={() => handleSort("requestedAt")}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => handleSort("requestedAt"))
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">
                      {t("workspace:requested_at")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("file_name")}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => handleSort("file_name"))
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">
                      {t("workspace:file_name")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("workspace:file_details")}
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("status")}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => handleSort("status"))
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">
                      {t("workspace:file_status")}
                    </div>
                  </th>
                  <th className="items-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                    {t("workspace:action")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {printRequestsData.results.map((request) => (
                  <tr
                    key={request.id}
                    id={`print-pdf-request-row-${request.id}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {request.requested_by
                        ? request.requested_by
                        : t("workspace:unavailable")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(request.requested_at).toLocaleString(
                        undefined,
                        {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        },
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {request.file_name
                        ? request.file_name
                        : t("workspace:unavailable")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col">
                        <span>
                          {request.file_details[0]?.recipient_count || 0}{" "}
                          {t("workspace:recipients")}
                        </span>
                        <span>
                          {request.file_details[0]?.file_size || "0 MB"}
                        </span>
                      </div>
                    </td>
                    <td
                      id={`print-pdf-request-status-${request.id}`}
                      className="px-6 py-4 whitespace-nowrap text-sm"
                    >
                      {request.status === "Completed" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />{" "}
                          {t("workspace:done")}
                        </span>
                      ) : request.status === "Failed" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle className="h-3 w-3 mr-1" />{" "}
                          {t("workspace:failed")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <AlertCircle className="h-3 w-3 mr-1" />{" "}
                          {t("workspace:pending")}
                        </span>
                      )}
                    </td>
                    <td className="pl-10 pr-6 py-4 whitespace-nowrap text-sm">
                      <a
                        id={`print-pdf-request-download-${request.id}`}
                        href={request.pdf_download}
                        download
                        aria-label={t("workspace:download")}
                        className={`h-8 w-8 p-0 ${
                          request.status !== "Completed" ||
                          !request.pdf_download ||
                          parseFloat(
                            request.file_details?.[0]?.file_size ?? "0",
                          ) <= 0
                            ? "text-gray-300 cursor-not-allowed pointer-events-none"
                            : "text-indigo-600 hover:text-indigo-800"
                        } flex items-center justify-center`}
                      >
                        <DownloadIcon
                          size={16}
                          className="inline-flex"
                          aria-hidden
                        />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex justify-center items-center h-64 bg-white rounded-lg">
            <div className="text-gray-700 font-semibold text-lg">
              {t("workspace:no_result_found")}
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 w-full">
          <button
            id="print-pdf-pagination-previous"
            onClick={handlePrevious}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={!printRequestsData.previous}
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                />
              </svg>
              <span>{t("workspace:previous")}</span>
            </span>
          </button>

          <button
            id="print-pdf-pagination-next"
            onClick={handleNext}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={!printRequestsData.next}
          >
            <span className="flex items-center justify-center gap-2">
              <span>{t("workspace:next")}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      {showPrintRequestModal && (
        <PrintRequestModal
          isOpen={showPrintRequestModal}
          onClose={() => setShowPrintRequestModal(false)}
          totalRecipients={documentData.total_recipients}
          isLoading={isTableLoading}
          onCreatePrintRequest={handleCreatePrintRequest}
        />
      )}
      <PrintCertificatePreview
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        UploadedPdfGif={documentData.certificate_preview}
        header={t("workspace:certificate_preview")}
        isPdf={true}
      />
      <DownloadRequestModal
        isOpen={showDownloadRequestModal}
        isSuccess={true}
        message={t("workspace:request_initiated")}
        header={t("workspace:success")}
        onClose={() => setShowDownloadRequestModal(false)}
      />
    </div>
  );
}
